import http from './http';

export interface LoginData {
  email: string;
  password: string;
  'cf-turnstile-response'?: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone?: string;
  phone_country?: string;
  'cf-turnstile-response'?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  requires_2fa?: boolean;
}

export interface RegisterResponse {
  user?: User;
  token?: string;
  pending?: boolean;
  email?: string;
  message?: string;
}

export interface Plan {
  id: number;
  name: string;
  slug: string;
  description: string;
  price: number;
  billing_cycle: string;
  api_key_limit: number;
  daily_api_key_limit: number;
  rate_limit: number;
  max_invoices: number;
  max_webhooks: number;
  features: string[];
  is_active: boolean;
}

export interface ApiKeyLimits {
  total_limit: number;
  daily_limit: number;
  total_used: number;
  daily_used: number;
  can_create: boolean;
}

export interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  phone_country?: string | null;
  phone_verified?: boolean;
  phone_verification_required?: boolean;
  email_panel_verification_required?: boolean;
  phone_panel_verification_required?: boolean;
  role: string;
  is_active: boolean;
  email_verified?: boolean;
  email_verified_at?: string | null;
  two_factor_enabled?: boolean;
  kyc_status?: 'not_submitted' | 'pending' | 'approved' | 'rejected';
  plan?: Plan;
  api_key_limits?: ApiKeyLimits;
}

export const authApi = {
  login: async (data: LoginData): Promise<AuthResponse> => {
    const response = await http.post('/login', data);
    return response.data;
  },

  register: async (data: RegisterData): Promise<RegisterResponse> => {
    const response = await http.post('/register', data);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await http.post('/v1/logout');
  },

  me: async (): Promise<User> => {
    const response = await http.get('/v1/me');
    return response.data;
  },

  resendVerificationEmail: async (): Promise<{ email_verification_sent_at?: string }> => {
    const response = await http.post('/v1/email/resend');
    return response.data;
  },

  verifyEmail: async (email: string, token: string): Promise<{ message: string; user?: User; token?: string }> => {
    const response = await http.post('/v1/email/verify', { email, token });
    return response.data;
  },

  requestEmailChange: async (newEmail: string, password: string): Promise<{ message: string }> => {
    const response = await http.post('/v1/email/change-request', { new_email: newEmail, password });
    return response.data;
  },

  verify2fa: async (code: string, cfTurnstileResponse?: string): Promise<AuthResponse> => {
    const payload: { code: string; 'cf-turnstile-response'?: string } = { code };
    if (cfTurnstileResponse) payload['cf-turnstile-response'] = cfTurnstileResponse;
    const response = await http.post('/v1/2fa/verify', payload);
    return response.data;
  },

  setup2fa: async (): Promise<{ secret: string; qr_code: string | null; qr_code_url: string }> => {
    const response = await http.get('/v1/2fa/setup');
    return response.data;
  },

  enable2fa: async (code: string): Promise<{ message: string }> => {
    const response = await http.post('/v1/2fa/enable', { code });
    return response.data;
  },

  disable2fa: async (password: string, code: string): Promise<{ message: string }> => {
    const response = await http.post('/v1/2fa/disable', { password, code });
    return response.data;
  },
};
